import { css, SerializedStyles } from '@emotion/core'
import parse from 'html-react-parser'
import * as React from 'react'

import Partners from '../components/Partners'
import { breakpoints } from '../constants/breakPoints'
import { isDesktop } from '../helpers/common'
import { useTranslation } from '../lang'
// Videos
import heroVideoMP4 from '../video/homepage.mp4'
import heroVideoWebm from '../video/homepage.webm'
import AdPartners from './ads/AdPartners'
import Block from './Block'
import { DemoButton, TourButton } from './Button'
import { ButtonBlock } from './ctas/CTAMain'
import Image from './Image'
import { ListBlock } from './ListBlock'
import { InstantTourType } from './useTracking'
import Video from './Video'

interface IntroProps {
  subHeading?: string
  heading?: string
  subtitle?: string
  withBetaLabel?: boolean
  withInstantTourButton?: boolean
  instantTourType?: InstantTourType
  listItems?: string[]
  heroVideo?: React.ReactNode
  imgSrc?: string
  subheadingIsH2?: boolean
  isAdPage?: boolean
  customBreakPoint?: number
  cssStyles?: SerializedStyles
}

export const Intro: React.FunctionComponent<IntroProps> = ({
  subHeading,
  heading,
  subtitle,
  withBetaLabel,
  withInstantTourButton,
  instantTourType,
  listItems,
  heroVideo,
  imgSrc,
  subheadingIsH2 = false,
  isAdPage = false,
  customBreakPoint = breakpoints.large,
  cssStyles,
}) => {
  const { t } = useTranslation()

  return (
    <React.Fragment>
      <Block
        columns={2}
        rowGap={0}
        columnGap={0}
        columnsTemplate="1fr 1.2fr"
        css={[blockStyle(customBreakPoint), !!cssStyles && cssStyles]}
      >
        <div css={introBlockStyle(customBreakPoint)}>
          {subheadingIsH2 ? (
            <h2 className="subtitle gray-text">
              {(subHeading || t('Intro.subheading')).toUpperCase()}{' '}
              {withBetaLabel && <span className="beta">Beta</span>}
            </h2>
          ) : (
            <h1 className="subtitle gray-text">
              {parse((subHeading || t('Intro.subheading')).toUpperCase())}{' '}
              {withBetaLabel && <span className="beta">BETA</span>}
            </h1>
          )}

          {subheadingIsH2 ? (
            <h1 className={isAdPage ? 'heading2 no-margin' : 'heading1 small'}>
              {parse(heading || t('Intro.heading'))}
            </h1>
          ) : (
            <h2 className={isAdPage ? 'heading2 no-margin' : 'heading1 small'}>
              {parse(heading || t('Intro.heading'))}
            </h2>
          )}
          <div css={introSubtitleStyle(customBreakPoint)} className="subtitle">
            {parse(subtitle || t('Intro.subtitle'))}
          </div>
          <div css={buttonContainerStyle(customBreakPoint)}>
            {withInstantTourButton ? (
              <ButtonBlock ghostButton={<TourButton type="ghost" instantTourType={instantTourType} />} />
            ) : (
              <DemoButton />
            )}
          </div>
          {listItems && (
            <ListBlock listItems={listItems} cssStyle={listBlockStyle(listItems.length, customBreakPoint)} />
          )}
        </div>
        {heroVideo ? (
          heroVideo
        ) : imgSrc ? (
          <Image src={imgSrc} alt={(subHeading || t('Intro.subheading')) ?? (heading || t('Intro.heading'))} />
        ) : (
          <div css={videoStyle(customBreakPoint)}>
            <Video
              srcType={[
                { src: heroVideoMP4, type: 'video/mp4' },
                { src: heroVideoWebm, type: 'video/webm' },
              ]}
              ratio={1.3}
              playInView={isDesktop(customBreakPoint || breakpoints.large + 1) ? false : true}
            />
          </div>
        )}
      </Block>
      {isAdPage ? <AdPartners isOnIntro css={partnersBlockStyle} /> : <Partners isOnIntro css={partnersBlockStyle} />}
    </React.Fragment>
  )
}

const blockStyle = (customBreakPoint: number) => css`
  @media screen and (max-width: ${customBreakPoint}px) {
    grid-template-columns: 1fr;
  }
`

const introBlockStyle = (customBreakPoint: number) => css`
  color: #1c2329;
  height: auto;
  padding: 4rem 0;

  .beta {
    padding: 0 0.5rem 0 0.5rem;
    margin-left: 0.25rem;
    transform: translateY(-3px);
  }

  @media screen and (max-width: ${customBreakPoint}px) {
    padding-bottom: 0;

    .heading1,
    .heading2,
    .subtitle,
    div {
      text-align: center !important;
    }
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    padding-top: 3rem;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    h2.subtitle.gray-text {
      margin-left: -0.5rem;
      margin-right: -0.5rem;
    }
  }
`

const buttonContainerStyle = (customBreakPoint: number) => css`
  margin: 1rem auto 0;

  @media screen and (max-width: ${customBreakPoint}px) {
    width: fit-content;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    width: initial;
  }
`

const partnersBlockStyle = css`
  display: block;
`

const introSubtitleStyle = (customBreakPoint: number) => css`
  color: #000;

  @media screen and (max-width: ${customBreakPoint}px) {
    max-width: 50rem;
    margin: 1.5rem auto 0.5rem !important;
  }
`

const listBlockStyle = (listItemsLength: number, customBreakPoint: number) => css`
  li {
    grid-template-columns: min-content max-content !important;
  }

  @media screen and (min-width: ${(listItemsLength % 2 ? customBreakPoint : breakpoints.small) + 1}px) {
    ${listItemsLength > 3 &&
    ` ul { 
          display: grid !important; 
          grid-template-columns: repeat(2, max-content); 
        }
      `}
  }

  ul {
    display: flex;
    align-items: center;
    gap: 0.75rem 2rem;
    flex-wrap: wrap;
    margin: 2rem 0 0 !important;

    li {
      margin-bottom: 0;
    }

    @media screen and (max-width: ${customBreakPoint}px) {
      justify-content: center;
    }

    @media screen and (max-width: ${listItemsLength > 2 ? '800' : breakpoints.small}px) {
      flex-direction: column;
      row-gap: 0.25rem;

      li {
        margin-bottom: 0.5rem;
      }
    }
  }
`

const videoStyle = (customBreakPoint?: number) => css`
  @media screen and (min-width: ${customBreakPoint || breakpoints.large + 1}px) {
    transform: translateX(0.5rem) translateY(1rem) scale(1.03);
  }

  @media screen and (min-width: ${breakpoints.medium + 1}px) and (max-width: ${customBreakPoint}px) {
    margin: 0 auto;
    width: 40rem;
  }
`
